import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft, Loader2, Save, Trash2, Shield, ShieldCheck, Eye } from "lucide-react";
import { api } from "../lib/api";

export default function AdminForum() {
  const [settings, setSettings] = useState(null);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [swearInput, setSwearInput] = useState("");

  const load = async () => {
    setLoading(true);
    try {
      const [s, u] = await Promise.all([
        api.get("/admin/forum/settings"),
        api.get("/admin/forum/users"),
      ]);
      setSettings(s.data);
      setUsers(u.data || []);
    } catch (e) {
      setError(e?.response?.data?.detail || "Failed to load.");
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, []);

  const save = async () => {
    setSaving(true);
    setError("");
    try {
      await api.put("/admin/forum/settings", {
        banner_text: settings.banner_text || "",
        banner_color: settings.banner_color || "#6CABDD",
        forum_title: settings.forum_title || "",
        forum_intro: settings.forum_intro || "",
        maintenance: !!settings.maintenance,
        swear_words: settings.swear_words || [],
      });
      await load();
    } catch (e) {
      setError(e?.response?.data?.detail || "Save failed.");
    } finally {
      setSaving(false);
    }
  };

  const addSwear = async () => {
    const w = swearInput.trim();
    if (!w) return;
    const next = Array.from(new Set([...(settings.swear_words || []), w]));
    // Optimistic UI + immediate persist so the list stays after refresh
    // without making the admin remember to hit "Save settings".
    setSettings((s) => ({ ...s, swear_words: next }));
    setSwearInput("");
    try {
      await api.put("/admin/forum/settings", { swear_words: next });
    } catch (e) {
      setError(e?.response?.data?.detail || "Could not save word.");
    }
  };
  const removeSwear = async (w) => {
    const next = (settings.swear_words || []).filter((x) => x !== w);
    setSettings((s) => ({ ...s, swear_words: next }));
    try {
      await api.put("/admin/forum/settings", { swear_words: next });
    } catch (e) {
      setError(e?.response?.data?.detail || "Could not remove word.");
    }
  };

  const setRole = async (userId, role) => {
    try {
      await api.post(`/forum/users/${userId}/role`, { role });
      await load();
    } catch (e) {
      setError(e?.response?.data?.detail || "Could not change role.");
    }
  };
  const banUser = async (userId) => {
    const reason = window.prompt("Ban reason") || "";
    await api.post(`/forum/users/${userId}/ban`, { reason });
    await load();
  };
  const unbanUser = async (userId) => {
    await api.post(`/forum/users/${userId}/unban`);
    await load();
  };
  const deleteUser = async (userId, nickname) => {
    const label = nickname ? `"${nickname}"` : "this user";
    if (!window.confirm(
      `Permanently delete ${label}?\n\n` +
      "• Their posts STAY on the timeline (shown as “(deleted)”).\n" +
      "• Their account row is removed, so next time they sign in they’ll be asked for a fresh nickname.\n\n" +
      "This cannot be undone."
    )) return;
    try {
      await api.delete(`/forum/users/${userId}`);
      await load();
    } catch (e) {
      setError(e?.response?.data?.detail || "Could not delete user.");
    }
  };

  const uploadBannerImage = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    const fd = new FormData();
    fd.append("file", file);
    try {
      const r = await api.post("/uploads", fd, { headers: { "Content-Type": "multipart/form-data" } });
      const url = r.data?.url;
      if (url) {
        setSettings((s) => ({ ...s, banner_image_url: url }));
        await api.put("/admin/forum/settings", { banner_image_url: url });
      }
    } catch (e2) {
      setError(e2?.response?.data?.detail || "Upload failed.");
    }
  };

  if (loading || !settings) {
    return <div className="flex min-h-screen items-center justify-center"><Loader2 className="animate-spin" /></div>;
  }

  return (
    <div className="min-h-screen bg-[var(--page-bg)]" data-testid="admin-forum">
      <div className="border-b border-black/10 bg-white">
        <div className="mx-auto flex max-w-5xl items-center justify-between px-5 py-4">
          <Link to="/admin" className="inline-flex items-center gap-2 text-sm" data-testid="admin-forum-back">
            <ArrowLeft size={16} /> Admin
          </Link>
          <h1 className="font-display text-xl font-black">Forum admin</h1>
          <button
            onClick={save}
            disabled={saving}
            data-testid="admin-forum-save"
            className="inline-flex items-center gap-2 rounded-md bg-[#0A0F17] px-3 py-1.5 font-display text-xs font-bold text-white hover:opacity-90 disabled:opacity-50"
          >
            <Save size={14} /> {saving ? "Saving…" : "Save settings"}
          </button>
        </div>
      </div>
      <main className="mx-auto max-w-5xl px-5 py-8 space-y-6">
        {error ? <p className="rounded-md bg-red-50 px-3 py-2 text-sm text-red-700">{error}</p> : null}

        {/* Identity */}
        <section className="rounded-md border border-black/10 bg-white p-5">
          <h2 className="font-display text-lg font-black">Identity</h2>
          <label className="mt-3 block">
            <span className="label-kicker text-[var(--brand-body)]">Forum name (title)</span>
            <input
              value={settings.forum_title || ""}
              onChange={(e) => setSettings((s) => ({ ...s, forum_title: e.target.value }))}
              maxLength={80}
              data-testid="admin-forum-title"
              placeholder="e.g. The LAST Post"
              className="mt-1 block w-full rounded-md border border-black/15 px-3 py-2 text-sm outline-none focus:border-[#0A0F17]"
            />
          </label>
          <label className="mt-3 block">
            <span className="label-kicker text-[var(--brand-body)]">Intro line (under the title)</span>
            <input
              value={settings.forum_intro || ""}
              onChange={(e) => setSettings((s) => ({ ...s, forum_intro: e.target.value }))}
              maxLength={300}
              data-testid="admin-forum-intro"
              className="mt-1 block w-full rounded-md border border-black/15 px-3 py-2 text-sm outline-none focus:border-[#0A0F17]"
            />
          </label>
        </section>

        {/* Banner image (full-width hero at top of forum) */}
        <section className="rounded-md border border-black/10 bg-white p-5">
          <h2 className="font-display text-lg font-black">Full-width banner image</h2>
          <p className="mt-1 text-xs text-[var(--brand-body)]">Shown above the forum content. Wide / panoramic images work best (5:1 ratio recommended).</p>
          {settings.banner_image_url ? (
            <div className="mt-3 overflow-hidden rounded-md border" data-testid="admin-forum-banner-image-preview">
              <img src={settings.banner_image_url} alt="banner" className="block w-full" style={{ aspectRatio: "5 / 1", objectFit: "cover" }} />
            </div>
          ) : null}
          <div className="mt-3 flex flex-wrap items-center gap-2">
            <label className="cursor-pointer rounded-md border border-black/15 bg-white px-3 py-1.5 font-display text-xs font-bold hover:border-[#0A0F17]" data-testid="admin-forum-banner-image-upload">
              {settings.banner_image_url ? "Replace image" : "Upload image"}
              <input type="file" accept="image/*" hidden onChange={uploadBannerImage} />
            </label>
            {settings.banner_image_url ? (
              <button
                type="button"
                onClick={async () => {
                  setSettings((s) => ({ ...s, banner_image_url: "" }));
                  await api.put("/admin/forum/settings", { banner_image_url: "" });
                }}
                data-testid="admin-forum-banner-image-clear"
                className="rounded-md border border-black/15 bg-white px-3 py-1.5 font-display text-xs font-bold text-red-600 hover:border-red-600"
              >
                Remove
              </button>
            ) : null}
          </div>
          <label className="mt-3 block">
            <span className="label-kicker text-[var(--brand-body)]">Make banner clickable (optional)</span>
            <input
              type="url"
              value={settings.banner_image_link || ""}
              onChange={(e) => setSettings((s) => ({ ...s, banner_image_link: e.target.value }))}
              onBlur={async (e) => {
                try { await api.put("/admin/forum/settings", { banner_image_link: e.target.value || "" }); }
                catch (err) { setError(err?.response?.data?.detail || "Could not save link."); }
              }}
              placeholder="https://… or /merch"
              data-testid="admin-forum-banner-link"
              className="mt-1 block w-full rounded-md border border-black/15 px-3 py-2 text-sm outline-none focus:border-[#0A0F17]"
            />
            <span className="mt-1 block text-[11px] text-[var(--brand-body)]">External URLs open in a new tab; on-site paths (starting with /) open in the same tab.</span>
          </label>
        </section>

        {/* Show / hide toggles */}
        <section className="rounded-md border border-black/10 bg-white p-5">
          <h2 className="font-display text-lg font-black">Show / hide on the forum page</h2>
          <p className="mt-1 text-xs text-[var(--brand-body)]">Tick what you want HIDDEN. Changes are saved instantly.</p>
          <div className="mt-3 grid gap-2 sm:grid-cols-2" data-testid="admin-forum-toggles">
            {[
              ["hide_title", "Hide forum title"],
              ["hide_intro", "Hide intro line"],
              ["hide_text_banner", "Hide coloured text banner"],
              ["hide_site_header", "Hide main site menu bar (replaces with a small ‘← LAST’ link)"],
              ["hide_action_bar", "Hide social-media action bar"],
              ["hide_mobile_chrome", "Hide BMC button + hamburger menu on mobile (forum only)"],
            ].map(([key, label]) => (
              <label key={key} className="inline-flex items-center gap-2 text-sm" data-testid={`admin-forum-toggle-${key}`}>
                <input
                  type="checkbox"
                  checked={!!settings[key]}
                  onChange={async (e) => {
                    const val = e.target.checked;
                    setSettings((s) => ({ ...s, [key]: val }));
                    try { await api.put("/admin/forum/settings", { [key]: val }); }
                    catch (err) { setError(err?.response?.data?.detail || "Could not save."); }
                  }}
                />
                {label}
              </label>
            ))}
          </div>
        </section>

        {/* Coloured text banner */}
        <section className="rounded-md border border-black/10 bg-white p-5">
          <h2 className="font-display text-lg font-black">Coloured text banner</h2>
          <input
            value={settings.banner_text || ""}
            onChange={(e) => setSettings((s) => ({ ...s, banner_text: e.target.value }))}
            maxLength={400}
            data-testid="admin-forum-banner-text"
            placeholder="Shown at the top of the forum"
            className="mt-3 block w-full rounded-md border border-black/15 px-3 py-2 text-sm outline-none focus:border-[#0A0F17]"
          />
          <div className="mt-3 flex items-center gap-3">
            <label className="text-xs text-[var(--brand-body)]">Colour</label>
            <input
              type="color"
              value={settings.banner_color || "#6CABDD"}
              onChange={(e) => setSettings((s) => ({ ...s, banner_color: e.target.value }))}
              data-testid="admin-forum-banner-color"
              className="h-8 w-12 rounded border border-black/15"
            />
            <span className="font-mono text-xs">{settings.banner_color}</span>
          </div>
        </section>

        {/* Maintenance */}
        <section className="rounded-md border border-black/10 bg-white p-5">
          <h2 className="font-display text-lg font-black">Maintenance mode</h2>
          <label className="mt-2 inline-flex items-center gap-2 text-sm">
            <input
              type="checkbox"
              checked={!!settings.maintenance}
              onChange={(e) => setSettings((s) => ({ ...s, maintenance: e.target.checked }))}
              data-testid="admin-forum-maintenance"
            />
            Hide the forum from non-moderators
          </label>
        </section>

        {/* Swear filter */}
        <section className="rounded-md border border-black/10 bg-white p-5">
          <h2 className="font-display text-lg font-black">Swear filter</h2>
          <p className="mt-1 text-xs text-[var(--brand-body)]">Each word is replaced with asterisks on post. Case-insensitive.</p>
          <div className="mt-3 flex gap-2">
            <input
              value={swearInput}
              onChange={(e) => setSwearInput(e.target.value)}
              onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); addSwear(); } }}
              placeholder="Add word"
              data-testid="admin-forum-swear-input"
              className="min-w-0 flex-1 rounded-md border border-black/15 px-3 py-2 text-sm outline-none focus:border-[#0A0F17]"
            />
            <button onClick={addSwear} className="rounded-md bg-[#0A0F17] px-3 py-2 font-display text-xs font-bold text-white">Add</button>
          </div>
          {(settings.swear_words || []).length > 0 ? (
            <ul className="mt-3 flex flex-wrap gap-2" data-testid="admin-forum-swear-list">
              {settings.swear_words.map((w) => (
                <li key={w} className="inline-flex items-center gap-1 rounded-full border border-black/15 bg-[#F8FAFC] px-3 py-1 font-mono text-xs">
                  {w}
                  <button onClick={() => removeSwear(w)} className="text-red-600" aria-label={`Remove ${w}`}>
                    <Trash2 size={12} />
                  </button>
                </li>
              ))}
            </ul>
          ) : null}
        </section>

        {/* Users / moderators */}
        <section className="rounded-md border border-black/10 bg-white p-5">
          <h2 className="font-display text-lg font-black">Users</h2>
          <p className="mt-1 text-xs text-[var(--brand-body)]">Promote trusted users to moderators (can delete posts + shadow-ban). Admins (you) can also ban accounts outright.</p>
          <ul className="mt-3 divide-y divide-black/5" data-testid="admin-forum-users">
            {users.map((u) => (
              <li key={u.id} className="flex flex-wrap items-center gap-3 py-3" data-testid={`admin-forum-user-${u.id}`}>
                <div className="min-w-0 flex-1">
                  <div className="font-display text-sm font-bold">{u.nickname || <span className="italic text-[var(--brand-body)]">(no nickname)</span>}</div>
                  <div className="font-mono text-[11px] text-[var(--brand-body)]">{u.id.slice(0, 8)} · joined {(u.created_at || "").slice(0, 10)}</div>
                  <div className="mt-1 flex flex-wrap items-center gap-2 text-[11px]">
                    {u.role === "admin" ? <span className="rounded-sm bg-[#0A0F17] px-1.5 py-0.5 font-bold uppercase text-white">Admin</span> : null}
                    {u.role === "mod" ? <span className="rounded-sm bg-[#FFA500]/20 px-1.5 py-0.5 font-bold uppercase text-[#B26F00]">Mod</span> : null}
                    {u.status === "banned" ? <span className="rounded-sm bg-red-100 px-1.5 py-0.5 font-bold uppercase text-red-700">Banned</span> : null}
                    {u.status === "shadowbanned" ? <span className="rounded-sm bg-gray-100 px-1.5 py-0.5 font-bold uppercase text-gray-600">Shadow</span> : null}
                  </div>
                </div>
                <div className="flex shrink-0 flex-wrap items-center gap-2">
                  {u.role !== "admin" ? (
                    u.role === "mod" ? (
                      <button onClick={() => setRole(u.id, "user")} className="rounded-md border border-black/15 px-2 py-1 font-display text-[11px] font-bold hover:border-[#0A0F17]" data-testid={`admin-forum-demote-${u.id}`}>
                        <Shield size={11} className="inline mr-1" /> Demote
                      </button>
                    ) : (
                      <button onClick={() => setRole(u.id, "mod")} className="rounded-md border border-black/15 px-2 py-1 font-display text-[11px] font-bold hover:border-[#0A0F17]" data-testid={`admin-forum-promote-${u.id}`}>
                        <ShieldCheck size={11} className="inline mr-1" /> Make mod
                      </button>
                    )
                  ) : null}
                  {u.status === "banned" ? (
                    <button onClick={() => unbanUser(u.id)} className="rounded-md border border-black/15 px-2 py-1 font-display text-[11px] font-bold text-green-700 hover:border-green-600">Unban</button>
                  ) : u.status === "shadowbanned" ? (
                    <button
                      onClick={() => unbanUser(u.id)}
                      data-testid={`admin-forum-unshadowban-${u.id}`}
                      title="Lift the shadow-ban — their posts become visible to everyone again immediately"
                      className="inline-flex items-center gap-1 rounded-md border border-black/15 px-2 py-1 font-display text-[11px] font-bold text-green-700 hover:border-green-600"
                    >
                      <Eye size={11} /> Remove shadow-ban
                    </button>
                  ) : null}
                  {u.status !== "banned" && u.role !== "admin" ? (
                    <button onClick={() => banUser(u.id)} className="rounded-md border border-black/15 px-2 py-1 font-display text-[11px] font-bold text-red-600 hover:border-red-600">Ban</button>
                  ) : null}
                  {u.role !== "admin" ? (
                    <button
                      onClick={() => deleteUser(u.id, u.nickname)}
                      data-testid={`admin-forum-delete-${u.id}`}
                      title="Permanently delete this user (posts will be hidden)"
                      className="inline-flex items-center gap-1 rounded-md border border-red-200 bg-red-50 px-2 py-1 font-display text-[11px] font-bold text-red-700 hover:border-red-600 hover:bg-red-100"
                    >
                      <Trash2 size={11} /> Delete
                    </button>
                  ) : null}
                </div>
              </li>
            ))}
            {users.length === 0 ? <li className="py-4 text-center text-sm text-[var(--brand-body)]">No forum users yet.</li> : null}
          </ul>
        </section>
      </main>
    </div>
  );
}
